import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { storage } from "@/lib/storage";
import { useLang } from "@/context/LanguageContext";
import { trackEvent } from "@/lib/analytics";
import { hasPlayBilling, playBuy, playRestore, playPriceLabels } from "@/lib/playBilling";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const PRICE_LABEL = "0,99 €";
export const BUNDLE_LABEL = "1,49 €";
const key = (product) => `atlas.ebook.${product}`;

export const startCheckout = async (product, lang) => {
  trackEvent("begin_checkout", { product, method: "stripe" });
  const { data } = await axios.post(`${API}/payments/checkout`, { lookup_key: product, origin_url: window.location.origin, lang });
  window.location.href = data.checkout_url;
};

/** Unlock state for one e-book: token in localStorage (validated), `?unlock=TOKEN` links, Play Billing in the Android app, Stripe in the browser. */
export default function useEbookAccess(product) {
  const { lang, t } = useLang();
  const [params, setParams] = useSearchParams();
  const [token, setToken] = useState(() => storage.get(key(product)) || null);
  const [buying, setBuying] = useState(false);
  const [receiptToken, setReceiptToken] = useState(null);
  const [prices, setPrices] = useState({});
  const unlockParam = params.get("unlock");
  const viaPlay = hasPlayBilling();

  useEffect(() => { if (viaPlay) playPriceLabels(["ebook_atlas", "ebook_cookbook", "ebook_bundle"]).then(setPrices).catch(() => {}); }, [viaPlay]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/payments/license/${token}`).then(({ data }) => {
      if (data.product !== product) throw new Error("product mismatch");
    }).catch((err) => {
      if (!err.response || err.response.status === 404 || err.message === "product mismatch") { storage.remove(key(product)); setToken(null); }
    });
  }, [token, product]);

  useEffect(() => {
    if (token || !viaPlay) return;
    playRestore().then((granted) => { if (granted.some((g) => g.product === product)) setToken(storage.get(key(product))); }).catch(() => {});
  }, [token, viaPlay, product]);

  useEffect(() => {
    if (!unlockParam) return;
    axios.get(`${API}/payments/license/${unlockParam}`).then(({ data }) => {
      if (data.product === product) {
        storage.set(key(product), unlockParam);
        setToken(unlockParam);
        toast.success(t("paywall_unlocked"));
      } else toast.error(t("paywall_wrong_book"));
    }).catch(() => toast.error(t("paywall_invalid_link"))).finally(() => { params.delete("unlock"); setParams(params, { replace: true }); });
  }, [unlockParam, product, params, setParams, t]);

  const buy = useCallback(async (sku = product) => {
    setBuying(true);
    try {
      if (viaPlay) {
        const data = await playBuy(sku);
        if (data.ok) { const tk = storage.get(key(product)); setToken(tk); toast.success(t("paywall_unlocked")); setReceiptToken(tk); } else toast.error(t("payment_failed"));
        setBuying(false);
      } else await startCheckout(sku, lang);
    } catch (err) {
      if (err?.name !== "AbortError") toast.error(t("payment_failed"));
      setBuying(false);
    }
  }, [product, viaPlay, lang, t]);

  const unlockWith = useCallback(() => setToken(storage.get(key(product))), [product]);

  const priceLabel = prices[product] || PRICE_LABEL;
  const bundleLabel = prices.ebook_bundle || BUNDLE_LABEL;
  return { unlocked: !!token, buy, buying, viaPlay, unlockWith, priceLabel, bundleLabel, receiptToken, closeReceipt: () => setReceiptToken(null) };
}

export const saveUnlock = (product, token) => storage.set(key(product), token);
